'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';

import { useQuantaLogin } from '@/hooks/api/quanta/useQuantaLogin';
import { cn } from '@/utils/cn';
import { Icons } from '@/components/icons';

import { getChevronColor, isSidebarItemActive, getIsQuantaPage } from '../../utils';

interface SidebarItemProps {
  name: string;
  href: string;
  icon?: React.ReactNode;
  subItems?: SidebarItemProps[];
}

const activeStyle = 'font-bold text-text-primary cursor-default';
const inactiveStyle = 'font-medium text-text-primary/50 hover:text-text-primary/80';

const SidebarItem = ({ name, href, icon, subItems }: SidebarItemProps) => {
  const pathname = usePathname();
  const { data: loginData } = useQuantaLogin({
    enabled: false,
  });

  const isNavigationEnabled = !!loginData?.data.user.user_id;
  const isActive = isSidebarItemActive(pathname, href);
  const isRootActive = pathname === href;

  return (
    <div className='flex flex-col'>
      <Link href={href} className={cn('flex items-center justify-between py-4', pathname === href ? activeStyle : inactiveStyle)}>
        <div className='flex items-center gap-3'>
          <div className='flex items-center justify-center min-w-[26px]'>{icon}</div>
          <span>{name}</span>
        </div>
        {subItems &&
          subItems.length > 0 &&
          (isActive ? (
            <Icons.chevronDown className={getChevronColor(isRootActive)} />
          ) : (
            <Icons.chevronUp className='text-text-primary/50' />
          ))}
      </Link>
      {subItems && subItems.length > 0 && isActive && (
        <div className='flex flex-col ml-[38px]'>
          {subItems.map(({ name: subName, href: subHref }, index) => {
            const isQuantaSubItem = getIsQuantaPage(href);
            const isDisabled = isQuantaSubItem && !isNavigationEnabled;
            const itemClassName = cn('py-4', pathname === subHref ? activeStyle : inactiveStyle, {
              'text-text-secondary cursor-not-allowed': isDisabled,
            });

            if (isDisabled) {
              return (
                <div key={index} className={itemClassName}>
                  <span className='leading-18 font-medium'>{subName}</span>
                </div>
              );
            }

            return (
              <Link key={index} href={subHref} className={itemClassName}>
                <span className='leading-18 font-medium'>{subName}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SidebarItem;
